/**
 * The two register filter controls that more than one screen draws.
 *
 * Lifted out of `AriregisterFilters.tsx` unchanged when the map grew its own
 * filter rail. The list arranges them in a horizontal toolbar and the map in a
 * vertical column, which is a layout difference and not a behaviour one - so
 * the layout stayed with each screen and the controls moved here, rather than
 * `AriregisterFilters` growing a `direction` prop that no reader could follow.
 */
import { DatePicker } from "@wa/ui";
import { ANY, fromIsoDate, toIsoDate } from "../../../lib/audienceFilter";
import { useI18n } from "../../../i18n/I18nProvider";
import { MenuField } from "../../ui";

/** A one-of-N register filter. `ANY` is the "no opinion" value rather than an
 *  empty string, so "the operator has not chosen" and "chose the blank option"
 *  cannot be confused. */
export function FilterField({
  value,
  onChange,
  options,
  anyLabel,
  label,
  className,
}: {
  value: string;
  onChange: (v: string) => void;
  options: { v: string; label: string }[];
  anyLabel: string;
  label: string;
  className?: string;
}) {
  return (
    <MenuField
      value={value}
      onChange={onChange}
      label={label}
      className={className}
      options={[{ value: ANY, label: anyLabel }, ...options.map((o) => ({ value: o.v, label: o.label }))]}
    />
  );
}

/**
 * "Licence valid since": one day, stored as an instant.
 *
 * Here rather than twice over for the reason this file exists - the list and
 * the map drew the identical control, and the pair had already drifted into two
 * copies of the same date arithmetic.
 *
 * Two conversions, and both used to be wrong. The filter stores an ISO instant
 * at LOCAL midnight while a day picker speaks `YYYY-MM-DD`, so the read side is
 * `fromIsoDate` and NOT `slice(0, 10)`: slicing reads the UTC day out of a
 * local-midnight instant, which is the day before east of Greenwich. The write
 * side parses at local midnight for the same reason, so a chosen day survives
 * being reopened.
 *
 * No dress class: `[data-slot="datepicker-trigger"]` is already the sunken 52px
 * pill, which is what `.crm-inp` gave this field before and what the number
 * pills beside it still wear. Only the width is local, and it is 10rem rather
 * than the 8.5rem the native input used to get: the shared trigger carries a
 * calendar icon and a clear disc that a native date field does not, and at
 * 8.5rem "03.08.2026" was measured truncating to "03.08...". The row it sits in
 * wraps, so the extra width is taken from the line rather than from the licence
 * dropdown beside it.
 */
export function LicenceSinceField({
  value,
  onChange,
  label,
}: {
  /** The stored instant, or null when the operator has not chosen a day. */
  value: string | null;
  onChange: (v: string | null) => void;
  label: string;
}) {
  const { locale } = useI18n();
  return (
    <DatePicker
      value={fromIsoDate(value)}
      onChange={(day) => onChange(day ? toIsoDate(new Date(`${day}T00:00:00`), false) : null)}
      locale={locale}
      placeholder={label}
      ariaLabel={label}
      className="w-40 min-w-0 shrink-0"
    />
  );
}

/**
 * Two numbers that bound one figure.
 *
 * `min-w-0` on both pills is not cosmetic. `.crm-inp` is a flex item, and a flex
 * or grid item's default `min-width: auto` is its content-based minimum - which
 * for a `<input type="number">` is the browser's `size="20"` width. Measured: the
 * pill refused to go below 218px even when its grid track was 0, so a pair of
 * them needed 484px and spilled over the column to its right. The input's own
 * `min-width: 0` (set in index.css) does not help, because it is the LABEL that
 * is the item being sized.
 */
export function RangeField({
  label,
  min,
  max,
  onMin,
  onMax,
  minPlaceholder,
  maxPlaceholder,
  unit,
}: {
  label: string;
  min: string;
  max: string;
  onMin: (v: string) => void;
  onMax: (v: string) => void;
  minPlaceholder: string;
  maxPlaceholder: string;
  unit?: string;
}) {
  return (
    <div className="min-w-0">
      <span className="crm-lab">{label}</span>
      <div className="flex min-w-0 items-center gap-1.5">
        <label className="crm-inp min-w-0 flex-1">
          <input type="number" min={0} inputMode="numeric" value={min} onChange={(e) => onMin(e.target.value)} placeholder={minPlaceholder} />
        </label>
        <span className="crm-mut shrink-0">-</span>
        <label className="crm-inp min-w-0 flex-1">
          <input type="number" min={0} inputMode="numeric" value={max} onChange={(e) => onMax(e.target.value)} placeholder={maxPlaceholder} />
        </label>
        {unit ? <span className="crm-mut shrink-0 text-[12.5px]">{unit}</span> : null}
      </div>
    </div>
  );
}
